package navigation.home.messages

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.Navigator
import cafe.adriel.voyager.transitions.SlideTransition
import com.essntl.components.sidepanel.SidePanel
import com.essntl.components.sidepanel.SizeConstraint
import com.essntl.components.sidepanel.Splitter
import com.essntl.components.sidepanel.rememberSidePanelState
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.message.domain.model.WhapiChatModel
import com.essntl.features.message.ui.MessageNavigation
import com.essntl.features.message.ui.MessageScreen
import com.essntl.features.message.ui.all_conversation.AllConversationEvent
import com.essntl.features.message.ui.all_conversation.rememberClient
import com.essntl.features.message.ui.all_conversation.rememberFilteredConversations
import com.essntl.features.message.ui.chat.ChatEvent
import com.essntl.features.message.ui.chat.rememberGroupParticipants
import com.essntl.toastmanager.Toast
import com.essntl.uikit.components.chat.contact.ContactUiState
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberFind
import com.essntl.uikit.theme.esColorScheme
import navigation.utils.NavLinkScreen

data class MessagesScreenNav(
    val conversationId: String? = null,
) : NavLinkScreen {
    override val defaultTitle: String = "Messages"

    override var navLinkState: NavLinkState by mutableStateOf(
        NavLinkState(
            title = defaultTitle,
            iconRes = EsIcons.Messages,
        ),
    )

    @Composable
    override fun Content() {
        val sidePanelState =
            rememberSidePanelState(
                sidePanelVisible = false,
                initialSidePanelSizeRatio = .2F,
                panelSizeConstraint =
                    SizeConstraint(
                        minSize = 300.dp,
                        maxSize = 600.dp,
                    ),
            )

        val allConversationScreenModel = koinScreenModel<AllConversationScreenModel>()
        val allConversationState by allConversationScreenModel.state.collectAsState()

        val chatScreenModel = koinScreenModel<ChatScreenModel>()
        val chatState by chatScreenModel.state.collectAsState()

        var clientPhoneNumber by remember { mutableStateOf<String?>(null) }
        var contactUiState by remember { mutableStateOf<ContactUiState?>(null) }

        val selectedConversation = rememberFind(
            allConversationState.allConversations,
            allConversationState.selectedConversationId,
        ) { conversation -> conversation.id }

        val client = allConversationState.rememberClient(selectedConversation?.phoneNumber)
            ?: ClientModel.empty.copy(
                telephone = selectedConversation?.phoneNumber,
                lastName = selectedConversation?.name ?: "",
            )

        val participants = chatState.rememberGroupParticipants()

        val selectedConversationUiState = remember(
            client,
            selectedConversation,
            allConversationState.getAllClient,
            chatState.getWhapiChat,
        ) {
            val contactClientUiState = client.toContactUiState
            selectedConversation?.toUiState(
                contact = contactClientUiState.copy(name = contactClientUiState.name ?: selectedConversation.name),
                participants = participants,
            )
        }

        val filteredConversations = allConversationState.rememberFilteredConversations()

        LaunchedEffect(Unit) {
            allConversationScreenModel.onEvent(AllConversationEvent.GetAllWhapiChats)
            if (conversationId != null)
                allConversationScreenModel.onEvent(AllConversationEvent.SelectConversation(conversationId))
            navLinkState = navLinkState.copy(
                onRefresh = {
                    allConversationScreenModel.onEvent(AllConversationEvent.GetAllWhapiChats)
                    if (conversationId != null)
                        allConversationScreenModel.onEvent(AllConversationEvent.SelectConversation(conversationId))
                },
            )
        }

        LaunchedEffect(allConversationScreenModel.toastManager) {
            allConversationScreenModel.toastManager.toastFlow.collect { toast ->
                if (toast is Toast.Message) {
                    allConversationScreenModel.onEvent(AllConversationEvent.GetAllWhapiChats)

                    if (allConversationState.selectedConversationId != null) {
                        chatScreenModel.onEvent(ChatEvent.GetWhapiChatById(allConversationState.selectedConversationId!!))
                        chatScreenModel.onEvent(ChatEvent.GetWhapiGroupById(allConversationState.selectedConversationId!!))
                        chatScreenModel.onEvent(ChatEvent.GetAllWhapiMessagesByChatId(allConversationState.selectedConversationId!!))
                        allConversationScreenModel.onEvent(AllConversationEvent.SelectConversation(allConversationState.selectedConversationId!!))
                    }
                }
            }
        }

        LaunchedEffect(allConversationState) {
            if (filteredConversations.isEmpty() || allConversationState.selectedConversationId != null)
                return@LaunchedEffect

            val defaultConversation = filteredConversations
                .firstOrNull { (it.unreadCount == 0) }
                ?: filteredConversations.first()

            if (defaultConversation.type == WhapiChatModel.ChatType.Contact)
                chatScreenModel.onEvent(ChatEvent.GetWhapiChatById(defaultConversation.id))
            else if (defaultConversation.type == WhapiChatModel.ChatType.Group)
                chatScreenModel.onEvent(ChatEvent.GetWhapiGroupById(defaultConversation.id))

            chatScreenModel.onEvent(ChatEvent.GetAllWhapiMessagesByChatId(defaultConversation.id))
            allConversationScreenModel.onEvent(AllConversationEvent.SelectConversation(defaultConversation.id))
        }

        LaunchedEffect(client) {
            client.telephone?.let { phoneNumber ->
                selectedConversationUiState?.let {

                    clientPhoneNumber = phoneNumber
                    contactUiState = it.contact

                    if (!clientPhoneNumber.isNullOrEmpty() &&
                        contactUiState != null
                    ) sidePanelState.sidePanelOpened = true
                }
            }
        }

        SidePanel(
            sidePanelState = sidePanelState,
            horizontalScroll = false,
            isHorizontal = true,
            sidePanel = {
                clientPhoneNumber?.let { p ->
                    contactUiState?.let { c ->
                        Navigator(
                            if (selectedConversation?.type == WhapiChatModel.ChatType.Contact)
                                ClientProfileScreenNav(p, c)
                            else GroupInfoScreenNav(
                                groupId = selectedConversation?.id ?: "",
                            ),
                        ) { navigator ->
                            Column(
                                modifier = Modifier
                                    .background(
                                        color = MaterialTheme.esColorScheme.bgSecondary,
                                        shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                                    )
                                    .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
                                    .fillMaxSize(),
                            ) {
                                EsMediumOutlineIconButton(
                                    onClick = {
                                        if (navigator.canPop)
                                            navigator.pop() else
                                            sidePanelState.sidePanelOpened = false
                                    },
                                    iconRes = if (navigator.canPop)
                                        EsIcons.ArrowLeft else
                                        EsIcons.ArrowRight,
                                    modifier = Modifier.padding(2.5f.spacing),
                                )

                                SlideTransition(navigator)
                            }
                        }
                    }
                }
            },
            splitter =
                Splitter(
                    measuredPart = {
                        Box(
                            modifier = Modifier.width(1.spacing).fillMaxHeight(),
                        )
                    },
                ),
            modifier = Modifier.fillMaxSize(),
        ) {
            MessageScreen(
                filteredConversations = filteredConversations,
                conversationState = allConversationState,
                onConversationEvent = allConversationScreenModel::onEvent,
                chatState = chatState,
                onChatEvent = chatScreenModel::onEvent,
                selectedConversation = selectedConversation,
                client = client,
                selectedConversationUiState = selectedConversationUiState,
                onNavigation = {
                    when (it) {
                        is MessageNavigation.ClientProfile -> {
                            clientPhoneNumber = it.clientPhoneNumber
                            contactUiState = it.contactUiState

                            if (!clientPhoneNumber.isNullOrEmpty() &&
                                contactUiState != null &&
                                selectedConversation?.type == WhapiChatModel.ChatType.Contact
                            )
                                sidePanelState.sidePanelOpened = true
                        }

                        is MessageNavigation.CloseSidePanel -> {
                            sidePanelState.sidePanelOpened = false
                        }
                    }
                },
            )
        }
    }
}